/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import logger from '@/utils/logger';
import { YOUTUBE_URL } from '@/utils/validatorRegex';

const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';
const YOUTUBE_THUMBNAIL_ORIGIN = 'https://i.ytimg.com';

const YOUTUBE_HOSTNAMES = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

/**
 * Returns true only when `url` parses as a URL whose hostname exactly matches
 * a known YouTube host. Substring matching is intentionally avoided so URLs
 * such as `https://attacker.com/youtube.com/...` are not misclassified —
 * otherwise they could end up as `<iframe src>` via downstream fallback paths.
 */
export const isYouTubeUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);
    return YOUTUBE_HOSTNAMES.has(hostname.toLowerCase());
  } catch {
    return false;
  }
};

const buildQueryString = (params?: Record<string, string | number>): string => {
  if (!params) {
    return '';
  }
  const entries = Object.entries(params);
  if (entries.length === 0) {
    return '';
  }
  const search = new URLSearchParams();
  for (const [key, value] of entries) {
    search.set(key, String(value));
  }
  return `?${search.toString()}`;
};

/**
 * Extracts the YouTube video ID from any supported URL format:
 *   - https://www.youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://www.youtube.com/embed/ID
 */
export const getYouTubeVideoId = (url: string): string | undefined => {
  if (url.includes('/embed/')) {
    return url.split('/embed/')[1]?.split(/[?&#]/)[0] || undefined;
  }
  return url.match(YOUTUBE_URL)?.[1];
};

export const getYouTubeEmbedUrl = (
  url: string,
  params?: Record<string, string | number>,
): string => {
  const query = buildQueryString(params);
  const videoId = getYouTubeVideoId(url);

  if (!videoId) {
    logger.warn({
      message: 'Failed to extract YouTube video ID; falling back to raw URL',
      meta: {
        url,
        paramsDropped: !!params && Object.keys(params).length > 0,
      },
    });
    return url;
  }

  return `${YOUTUBE_EMBED_ORIGIN}/embed/${videoId}${query}`;
};

export const getYouTubeThumbnailUrl = (url: string): string | undefined => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) {
    return undefined;
  }
  return `${YOUTUBE_THUMBNAIL_ORIGIN}/vi/${videoId}/hqdefault.jpg`;
};
